import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useListSearchParams } from '@/hooks/useListSearchParams'
import { useGenerations, useDeleteGeneration } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pencil, Trash2, Video } from 'lucide-react'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { toast } from 'sonner'

export function GenerationList() {
  const navigate = useNavigate()
  const { page, filters, setPage, setFilter } = useListSearchParams({ search: '' })
  const [searchInput, setSearchInput] = useState(filters.search)

  // Debounce search input -> sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilter('search', searchInput)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, filters.search, setFilter])

  const { data, isLoading, error } = useGenerations({
    page,
    pageSize: 20,
    search: filters.search || undefined,
    sortBy: 'created_at',
    sortOrder: 'desc',
  })

  const deleteMutation = useDeleteGeneration()

  const handleDelete = async (id: string, prompt: string) => {
    const truncatedPrompt = prompt.length > 50 ? `${prompt.substring(0, 50)}...` : prompt
    if (!confirm(`"${truncatedPrompt}" 비디오를 삭제하시겠습니까?`)) return

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('비디오가 삭제되었습니다')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '삭제 중 오류가 발생했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">생성된 비디오</h1>
          <p className="text-caption mt-2 leading-relaxed">AI로 생성된 비디오 관리</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <Input
          placeholder="프롬프트 검색..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 min-w-[800px]">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                비디오
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                프롬프트
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[80px]">
                상태
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                비율
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase min-w-[90px]">
                프레임
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                생성일시
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.items.map((generation) => (
              <tr
                key={generation.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/generations/${generation.id}/view`)}
              >
                <td className="px-6 py-4">
                  <div className="relative w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                    {(() => {
                      const thumbnailUrl = generation.generatedVideo?.thumbnailUrls?.[0]
                        ?? generation.generatedVideo?.thumbnailUrl
                      if (!thumbnailUrl) {
                        return <Video className="w-6 h-6 text-gray-400" />
                      }
                      return (
                        <ImageWithFallback
                          src={thumbnailUrl}
                          alt="생성된 비디오"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )
                    })()}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900 line-clamp-2">
                    {generation.prompt}
                  </div>
                  {generation.user?.email && (
                    <div className="text-xs text-gray-400 mt-1">
                      {generation.user.email}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    const s = generation.status ?? 'completed'
                    const map: Record<string, { label: string; className: string }> = {
                      processing: { label: '생성 중', className: 'bg-blue-100 text-blue-700' },
                      completed: { label: '완료', className: 'bg-emerald-100 text-emerald-700' },
                      failed: { label: '실패', className: 'bg-red-100 text-red-700' },
                      cancelled: { label: '취소', className: 'bg-slate-100 text-slate-500' },
                    }
                    const badge = map[s] ?? { label: s, className: 'bg-gray-100 text-gray-600' }
                    return (
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                          {badge.label}
                        </span>
                        {generation.deletedAt && (
                          <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                            삭제됨
                          </span>
                        )}
                      </div>
                    )
                  })()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700 text-center">
                  {generation.ratio}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700 text-center">
                  {generation.frameImages?.length || 0}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                  {(() => {
                    const date = new Date(generation.createdAt)
                    const month = String(date.getMonth() + 1).padStart(2, '0')
                    const day = String(date.getDate()).padStart(2, '0')
                    const hours = String(date.getHours()).padStart(2, '0')
                    const minutes = String(date.getMinutes()).padStart(2, '0')
                    return `${month}-${day} ${hours}:${minutes}`
                  })()}
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/admin/generations/${generation.id}/edit`}>
                      <Button variant="ghost" size="sm">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(generation.id, generation.prompt)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data?.items.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            생성된 비디오가 없습니다
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            이전
          </Button>
          <span className="text-sm text-gray-600">
            {page} / {data.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
            disabled={page === data.pagination.totalPages}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  )
}
